<script lang="ts">
	import type { CloudImage } from "$lib/def/seo/cloud-image";
		
	import { getCimgUrl } from "$lib/serv/admin/editImg";
  import { qrcode } from "$lib/serv/admin/editLink";
	import { imgSrc, imgSrcSet } from "$lib/serv/image-details";
	import { geoOpted$, setGeoOpted } from "$lib/serv/locations";
	import { selectAddress } from '$lib/ui/modals/AddressModal.svelte';
	
	import Input from "$lib/ui/form/Input.svelte";
  import LazyImage from "$lib/ui/shared/LazyImage.svelte";

	export let image: string | undefined;
	export let title: string | undefined | null;
	export let subtitle: string | undefined | null = undefined;
	export let boldSubtitle: boolean = false;
	export let copy: string | undefined = undefined;
	export let icon: CloudImage | null = null;
	export let type: 'general' | 'scan' | 'action' | 'form' = 'general';
	export let action: {
		cta: (() => void) | string,
		btnText: string | undefined,
		newTab?: boolean
	} | null = null;
	export let noButtons = false;
	export let appstores: [string?, string?][] = [];

	async function currentLocation() {
		setGeoOpted();
		selectAddress();
	}

</script>

<div class="seo-header {type}">
	<div class="img-container">
		<LazyImage {...imgSrcSet(image, 0.5)} alt={title ?? 'banner'} />
		{#if icon}
			<div class="logo"><img src={getCimgUrl(icon)} alt="logo"></div>
		{/if}
	</div>
	<section>
		<section>
			<h1 class="display-l bold">{title}</h1>
			{#if subtitle}<p class="body-l {boldSubtitle ? 'bold' : ''}">{subtitle}</p>{/if}
			{#if copy}<p class="body-l">{copy}</p>{/if}
		</section>
		<article>
			{#if action}
				{#if typeof action.cta === 'string'}
					<a class="button" href={action.cta} target="{action.newTab ? '_blank' : '_self'}">{action.btnText}</a>
				{:else}
					{#if type === 'form'}
						<div>
							<Input label='Enter your restaurant name' />
						</div>
					{/if}
					<button class="button" on:click={action.cta}>{action.btnText}</button>
				{/if}
			{:else}
				{#if !noButtons}
					<button class="button label-xl" on:click={() => selectAddress()}>
						&nbsp;<span class="icon-pin-outline-20">&nbsp;</span>Enter address
					</button>
					<!-- TODO: find out if this can be changed - this feature is available when you click the enter address button -->
					{#if !$geoOpted$}
						<button class="button tertiary label-xl" on:click={(e) => currentLocation()}>
							&nbsp;<span class="icon-current-location-20">&nbsp;</span>Use current location
						</button>
					{/if}
				{/if}
			{/if}
			{#if type === 'scan'}
				{#if appstores}
					<div class="qrs">
						{#each appstores as [img,url]}
							<a href={url} target="_blank">
								<img src={imgSrc(img)} alt="App store">
								{#if url}
									<canvas use:qrcode={[url]}></canvas>
								{/if}
							</a>
						{/each}
					</div>
				{/if}
			{/if}
		</article>
	</section>
</div>

<style lang="scss">
	@use 'screen';
	@use 'color';

	.seo-header {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding-bottom: 2rem;
		@media(min-width: 820px) { flex-direction: row; }
		>div {
			position: relative;
			width: 100%;
			height: 100%;
			aspect-ratio: 16 / 9;
			border-radius: 22px;
			margin-bottom: 2.3rem;
			border: 1px solid var(--color-separator);
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
			@media(min-width: 820px) { max-width: 560px; margin-bottom: unset; }
			@media(screen.$md) { width: 99%; }
			>:global(img) {
				position: absolute;
				width: 100%;
				height: 100%;
				object-fit: cover;
				border-radius: 21px;
			}
			.logo {
				position: absolute;
				top: calc(100% - 44px);
				left: 2rem;
				width: 60px;
				height: 60px;
				background: color.$white;
				padding: .1rem;
				border: 1px solid color.$separator;
				border-radius: 14px;
				display: flex;
				justify-content: center;
				align-items: center;
				>img { border-radius: 10px; }
				@media(min-width: 820px) {
					top: 50px;
					left: calc(100% - 44px);
				}
			}
		}
		>section {
			@media(min-width: 820px) { padding-left: 3rem; }
			@media(screen.$md) { width: 100%; }
			@media(screen.$mdl) {
				display: flex;
				flex-direction: column;
				justify-content: center;
			}
			@media(screen.$xl) { max-width: 800px; width: unset; }
			section {
				@media(screen.$lg) { padding-right: 4rem; }
				h1 {
					line-height: 2.6rem;
					@media(screen.$sm) { line-height: 3.5rem; }
					@media(min-width: 820px) { line-height: 2.8rem; font-size: 2.3rem; }
					@media(screen.$xl) { line-height: 3.2rem; font-size: 2.6rem; }
				}
				p { color: color.$on-background-high-importance; }
			}
			article {
				margin-top: 1.5rem;
				@media(min-width: 500px) {
					display: flex;
					align-items: center;
				}
				@media(min-width: 820px) {
					flex-direction: column;
					align-items: flex-start;
				}
				@media(screen.$md) { flex-direction: row; align-items: center; }
				@media(screen.$lg) { margin-top: 1.5rem; }
				button {
					display: block;
					margin: 0;
					&:nth-of-type(1) { 
						margin: 0 1rem 1rem 0;
						@media(min-width: 500px) { margin: 0 1rem 0 0; }
						@media(min-width: 820px) { margin: 0 1rem 1rem 0; }
						@media(screen.$md) { margin: 0 1rem 0 0; }
					}
					&:nth-of-type(2) { text-wrap: nowrap; padding: 10px 14px; }
					span { margin-right: .5rem; }
				}
			}
		}
		// For headers with QR codes
		&.scan {
			>div {
				aspect-ratio: 1 / 1;
				@media(min-width: 820px) { max-width: 360px; }
			}
			>section {
				@media(screen.$md) { width: unset; }
				section {
					h1 { margin-bottom: .5rem; }
					p { margin-top: .5rem; }
				}
				article {
					.qrs {
						display: flex;
						flex-direction: row;
						justify-content: center;
						align-items: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 0;
						@media(min-width: 820px) {
							flex-direction: row;
							justify-content: flex-start;
						}
						a {
							display: flex;
							flex-direction: column;
							align-items: center;
							border: 2px solid color.$separator;
							border-radius: 14px;
							background: color.$white;
							margin: 0 0 1rem 0;
							padding: .6rem;
							width: 150px;
							height: 56px;
							overflow: hidden;
							@media(min-width: 820px) {
								margin: 0 .5rem 0 0;
								&:last-child { margin-right: unset; }
							}
							@media(min-width: 890px) {
								height: unset;
							}
							@media(screen.$md) {
								margin: 0 2rem 0 0;
							}
							img, canvas {
								&:nth-child(1) {
									width: inherit;
									height: 40px;
								}
								&:nth-child(2) {
									display: none;
									width: 80px;
									aspect-ratio: 1/1;
									margin: 0.4rem 0 0 0;
									border-radius: 12px;
									@media(min-width: 890px) {
										width: 110px !important;
										height: 110px !important;
										display: block;
									}
								}
							}
							canvas {
								width: 110px;
								height: 110px;
							}
						}
					}
				}
			}
		}
		&.form {
			>section {
				article {
					display: flex;
					flex-direction: column;
					gap: 1rem;
					@media(screen.$md) {
						flex-direction: row;
						align-items: center;
					}
					div {
						width: 100%;
						@media(screen.$mdl) { max-width: 500px; }
					}
					button {
						margin: 0;
						@media(screen.$md) { width: unset; }
					}
				}
			}
		}
	}
</style>
